import { Pipe, PipeTransform, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import Autolinker from 'autolinker';
import { TranslateService } from '@ngx-translate/core';
import { ChatLoggingService } from '../services/chat-logging.service';

@Pipe({
  name: 'unreadMessageDate',
  standalone: false
})
export class UnreadMessageDatePipe implements PipeTransform {
  transform(date: any, locale: string = 'en'): string {
    if (date) {
      var currentDate = new Date();
      var unreadDate = new Date(date);
      if (
        unreadDate.getDate() == currentDate.getDate() &&
        unreadDate.getMonth() == currentDate.getMonth() &&
        unreadDate.getFullYear() == currentDate.getFullYear()
      ) {
        try {
          return '' + new DatePipe(locale).transform(unreadDate, 'shortTime');
        } catch {
          return '' + new DatePipe('en').transform(unreadDate, 'shortTime');
        }
      } else {
        try {
          return (
            '' +
            new DatePipe(locale).transform(unreadDate, 'mediumDate') +
            ', ' +
            new DatePipe(locale).transform(unreadDate, 'shortTime')
          );
        } catch {
          return (
            '' +
            new DatePipe('en').transform(unreadDate, 'mediumDate') +
            ', ' +
            new DatePipe('en').transform(unreadDate, 'shortTime')
          );
        }
      }
    }
    return '';
  }
}

@Pipe({
  name: 'attachmentDate',
  standalone: false
})
export class AttachmentDatePipe implements PipeTransform {
  transform(date: any, locale: string = 'en'): string {
    if (date) {
      var currentDate = new Date();
      var unreadDate = new Date(date);
      if (
        unreadDate.getDate() == currentDate.getDate() &&
        unreadDate.getMonth() == currentDate.getMonth() &&
        unreadDate.getFullYear() == currentDate.getFullYear()
      ) {
        try {
          return '' + new DatePipe(locale).transform(unreadDate, 'h:mm a');
        } catch {
          return '' + new DatePipe('en').transform(unreadDate, 'h:mm a');
        }
      } else {
        try {
          return '' + new DatePipe(locale).transform(unreadDate, 'shortDate');
        } catch {
          return '' + new DatePipe('en').transform(unreadDate, 'shortDate');
        }
      }
    }
    return '';
  }
}

@Pipe({
  name: 'chatDate',
  pure: false,
  standalone: false
})
export class ChatDatePipe implements PipeTransform {
  constructor(public translateService: TranslateService) {}

  transform(date: any, locale: string = 'en', showDayOfWeek: boolean = false): string {
    if (date) {
      var currentDate = new Date();
      currentDate.setHours(23, 59, 59, 999);
      var yesterdayDate = new Date();
      yesterdayDate.setHours(23, 59, 59, 999);
      yesterdayDate.setDate(currentDate.getDate() - 1);
      var weekOldDate = new Date();
      weekOldDate.setHours(23, 59, 59, 999);
      weekOldDate.setDate(currentDate.getDate() - 7);
      var yearOldDate = new Date();
      //Let's just set a year ago to the last day of the current month last year
      yearOldDate.setHours(23, 59, 59, 999);
      yearOldDate.setDate(1);
      yearOldDate.setFullYear(currentDate.getFullYear() - 1);
      yearOldDate.setDate(new Date(yearOldDate.getFullYear(), yearOldDate.getMonth() + 1, 0).getDate());

      var setDate = new Date(date);
      if (
        setDate.getDate() == currentDate.getDate() &&
        setDate.getMonth() == currentDate.getMonth() &&
        setDate.getFullYear() == currentDate.getFullYear()
      ) {
        // Date is today
        // Ex. showDayOfWeek: false (default) - TODAY • APRIL 2, 2024, 2:31 PM
        // Ex. showDayOfWeek: true - TUESDAY • APRIL 2, 2024, 2:31 PM
        var todayString = showDayOfWeek
          ? new DatePipe(locale).transform(setDate, 'EEEE')
          : this.translateService.instant('cl_chat.today');
        try {
          return (
            todayString +
            ' • ' +
            new DatePipe(locale).transform(setDate, 'longDate') +
            ', ' +
            new DatePipe(locale).transform(setDate, 'shortTime')
          );
        } catch {
          return (
            todayString +
            ' • ' +
            new DatePipe('en').transform(setDate, 'longDate') +
            ', ' +
            new DatePipe('en').transform(setDate, 'shortTime')
          );
        }
      } else if (
        setDate.getDate() == yesterdayDate.getDate() &&
        setDate.getMonth() == yesterdayDate.getMonth() &&
        setDate.getFullYear() == yesterdayDate.getFullYear()
      ) {
        // Date is yesterday
        // Ex. showDayOfWeek: false (default) - YESTERDAY • APRIL 1, 2024, 9:49 AM
        // Ex. showDayOfWeek: true - MONDAY • APRIL 1, 2024, 9:49 AM
        var yesterdayString = showDayOfWeek
          ? new DatePipe(locale).transform(setDate, 'EEEE')
          : this.translateService.instant('cl_chat.yesterday');
        try {
          return (
            yesterdayString +
            ' • ' +
            new DatePipe(locale).transform(setDate, 'longDate') +
            ', ' +
            new DatePipe(locale).transform(setDate, 'shortTime')
          );
        } catch {
          return (
            yesterdayString +
            ' • ' +
            new DatePipe('en').transform(setDate, 'longDate') +
            ', ' +
            new DatePipe('en').transform(setDate, 'shortTime')
          );
        }
      } else if (yearOldDate > setDate) {
        // Date is older than 1 year
        // Ex. FEBRUARY 15, 2023
        try {
          return '' + new DatePipe(locale).transform(setDate, 'longDate');
        } catch {
          return '' + new DatePipe('en').transform(setDate, 'longDate');
        }
      } else if (weekOldDate > setDate) {
        // Date is older than 1 week but less than or equal to 1 year
        // Ex. MARCH 6, 2024, 11:03 AM
        try {
          return (
            '' +
            new DatePipe(locale).transform(setDate, 'longDate') +
            ', ' +
            new DatePipe(locale).transform(setDate, 'shortTime')
          );
        } catch {
          return (
            '' +
            new DatePipe('en').transform(setDate, 'longDate') +
            ', ' +
            new DatePipe('en').transform(setDate, 'shortTime')
          );
        }
      } else {
        // Date is greater than yesterday and less than 1 week
        // Ex. FRIDAY, MARCH 29, 2024, 2:21 PM
        try {
          return (
            '' +
            new DatePipe(locale).transform(setDate, 'fullDate') +
            ', ' +
            new DatePipe(locale).transform(setDate, 'shortTime')
          );
        } catch {
          return (
            '' +
            new DatePipe('en').transform(setDate, 'fullDate') +
            ', ' +
            new DatePipe('en').transform(setDate, 'shortTime')
          );
        }
      }
    }
    return '';
  }
}

@Pipe({
  name: 'linky',
  standalone: false
})
export class LinkyPipe implements PipeTransform {
  private chatLoggingService = inject(ChatLoggingService);

  transform(value: any): any {
    let retVal: any = value;
    //Autolinker recognizes values that contain 'zip' as a link
    //node_modules\autolinker\dist\commonjs\matcher\tld-regex.js
    if (typeof value === 'string' && !value.includes('.zip')) {
      try {
        if (value.indexOf('<iframe') === -1)
          retVal = Autolinker.link(value, {
            newWindow: true,
            urls: true,
            stripPrefix: false,
            email: true,
            phone: false,
            mention: false,
            hashtag: false,
            stripTrailingSlash: false
          });
      } catch (error) {
        this.chatLoggingService.warn({ logMessage: 'linky-pipe.transform()...', logParameters: [value, error] });
      }
    }
    return retVal;
  }
}

@Pipe({
  name: 'truncate',
  standalone: false
})
export class TruncatePipe implements PipeTransform {
  transform(value: string, limit = 25, completeWords = false, ellipsis = '...') {
    if (!value) {
      value = '';
    }
    if (completeWords) {
      limit = value.substr(0, 13).lastIndexOf(' ');
    }

    let result = value.substr(0, limit);
    if (result.length >= limit) {
      result += ellipsis;
    }

    return result;
  }
}

@Pipe({
  name: 'minuteSeconds',
  standalone: false
})
export class MinuteSecondsPipe implements PipeTransform {
  transform(value: number): string {
    const minutes: number = Math.floor(value / 60);
    return (
      minutes.toString().padStart(2, '0') +
      ':' +
      Math.floor(value - minutes * 60)
        .toString()
        .padStart(2, '0')
    );
  }
}

@Pipe({
  name: 'slugify',
  standalone: false
})
export class SlugifyPipe implements PipeTransform {
  transform(value: string): string {
    return value.replace(/ /g, '-').toLowerCase();
  }
}
